import { Children, ReactNode } from "react"
import { Tabs as RATabs, RouterProvider } from "react-aria-components"
import { TabsItem } from "./TabsItem"
import { TabsProps } from "./types"
import { isTabItem } from "./utils"
import { TabsInner } from "./TabsInner"

/**
 * Wraps given children in react-aria RouterProvider when navigate prop is defined. This allows Tabs.Items with a `href` to access router library's navigate function
 */
const MaybeRouterProvider = ({
  children,
  navigate,
}: {
  navigate?: (path: string) => void
  children: ReactNode
}) => {
  if (navigate) {
    return <RouterProvider navigate={navigate}>{children}</RouterProvider>
  }

  return <>{children}</>
}

export const Tabs = ({
  children,
  className,
  defaultSelectedTab,
  label,
  navigate,
  onTabChange,
  selectedTab,
  ...rest
}: TabsProps) => {
  const disabledKeys: string[] =
    Children.map(children, child =>
      isTabItem(child) && child.props.disabled ? child.props.id : undefined,
    ) || []

  return (
    <MaybeRouterProvider navigate={navigate}>
      <RATabs
        slot={null} // When tabs are nested in tabs, this ensures that the context of a nested tabs component are not influenced by a parent tabs component
        className={className}
        defaultSelectedKey={defaultSelectedTab}
        disabledKeys={disabledKeys}
        onSelectionChange={key =>
          onTabChange?.(key as string /* User can only pass a string as an id */)
        }
        selectedKey={selectedTab}
        {...rest}
      >
        <TabsInner label={label}>{children}</TabsInner>
      </RATabs>
    </MaybeRouterProvider>
  )
}

Tabs.displayName = "Tabs"

Tabs.Item = TabsItem
